import * as React from "react";
import { connect } from "react-redux";
import { QueryTable, QueryTableColumn } from "../../misc/components/queryTable/QueryTable";
import { Query, User, QueryCondition, Permission } from "../../misc/api/apiTypes";
import MultiSelectModal from '../../misc/components/queryTable/MultiSelectModal';
import { HamburgerMenu } from "../../misc/components/HamburgerMenu";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { Authorization, GlobalAuthorization } from "../../misc/components/Authorization";
import Condition from "../../misc/components/Condition";
import { MainState } from "../../Core/reducers";
import BooleanSelectModal from "../../misc/components/queryTable/BooleanSelectModal";
import { QueryTableState, ReceiveQueryAction, PageAction, SortAction, FilterAction } from "../../misc/components/queryTable/Hooks/UseQueryTable";
import { useSecurityContext } from "../../Core/SecurityContext";
import { Link } from "react-router-dom";
import { useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import { uniqBy } from "../../misc/ArrayFunctions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';

interface UserListTableState {
    showEmailFilter: boolean;
    showFirstNameFilter: boolean;
    showLastNameFilter: boolean;
    showIsDisabledFilter: boolean;
    emailFilterQuery: Query<User>;
}

const initialUserTableState: UserListTableState = {
    showEmailFilter: false,
    showFirstNameFilter: false,
    showLastNameFilter: false,
    showIsDisabledFilter: false,
    emailFilterQuery: {
        Filters: [],
        Members: ['Id', 'Email', 'FirstName', 'LastName'],
    },
}

export interface UserListTableProps {
    queryTableReducer: [QueryTableState, React.Dispatch<any>];
    currentStudyId: string;
}

export const UserListTable = ({ queryTableReducer, currentStudyId }: UserListTableProps) => {
    const { currentUserPermissions } = useSecurityContext();
    const [queryTableState, dispatch] = queryTableReducer;
    const legacyClient = useWebPortalLegacyClient();
    const [isLoading, setIsLoading] = React.useState(false);
    const [filterValues, setFilterValues] = React.useState<{
        Email: string[],
        FirstName: string[],
        LastName: string[],
        IsDisabled: string[]
    }>({
        Email: [],
        FirstName: [],
        LastName: [],
        IsDisabled: []
    });

    React.useEffect(() => {
        let mounted = true;
        const fetchUsers = async () => {
            if (mounted) {
                setIsLoading(true);
                const userQuery = await legacyClient.getUsers(queryTableState.query);
                dispatch(ReceiveQueryAction.create(userQuery));
                setIsLoading(false);
            }
        }

        fetchUsers();

        return () => { mounted = false };
    }, [queryTableState.query.Page.Current, queryTableState.query.Order.Member, queryTableState.query.Order.Direction, queryTableState.query.Filters]);

    // Non-generic
    const userReducer = (state: UserListTableState, action): UserListTableState => {
        switch (action.type) {
            case 'SetEmailsFilterVisiblity':
                return {
                    ...state,
                    showEmailFilter: action.isVisible,
                };
            case 'SetFirstNameFilterVisiblity':
                return {
                    ...state,
                    showFirstNameFilter: action.isVisible,
                };
            case 'SetLastNameFilterVisiblity':
                return {
                    ...state,
                    showLastNameFilter: action.isVisible,
                };
            case 'SetIsDisabledFilterVisiblity':
                return {
                    ...state,
                    showIsDisabledFilter: action.isVisible,
                };
            case 'ReceiveEmails':
                return {
                    ...state,
                    emailFilterQuery: action.query,
                };
            default:
                throw new Error("Unexpected action");
        }
    }

    const [currentUserTableState, dispatchUser] = React.useReducer(userReducer, initialUserTableState);

    React.useEffect(() => {
        const fetchEmails = async () => {
            const emailsQuery = await legacyClient.getUsers(currentUserTableState.emailFilterQuery);

            dispatchUser({ type: 'ReceiveEmails', query: emailsQuery });
        }

        fetchEmails();
    }, [])

    React.useEffect(() => {
        const updateTempFilters = (field: keyof typeof filterValues, show: boolean) => {
            if (!show) return;

            const values = getFilterValues(field, QueryCondition.Equal) || [];
            setFilterValues(prev => ({
                ...prev,
                [field]: values
            }));
        };

        updateTempFilters("Email", currentUserTableState.showEmailFilter);
        updateTempFilters("FirstName", currentUserTableState.showFirstNameFilter);
        updateTempFilters("LastName", currentUserTableState.showLastNameFilter);
        updateTempFilters("IsDisabled", currentUserTableState.showIsDisabledFilter);
    }, [
        currentUserTableState.showEmailFilter,
        currentUserTableState.showFirstNameFilter,
        currentUserTableState.showLastNameFilter,
        currentUserTableState.showIsDisabledFilter
    ]);

    const handlePage = (page: number) => {
        !isLoading && dispatch(PageAction.create(page));
    }

    const handleSort = (member: string) => {
        dispatch(SortAction.create(member));
    }

    const handleFilter = (member: keyof typeof filterValues) => {
        const existing = getFilterValues(member, QueryCondition.Equal) || [];
        setFilterValues(prev => ({ ...prev, [member]: existing }));

        switch (member) {
            case 'Email':
                dispatchUser({ type: 'SetEmailsFilterVisiblity', isVisible: true });
                break;
            case 'FirstName':
                dispatchUser({ type: 'SetFirstNameFilterVisiblity', isVisible: true });
                break;
            case 'LastName':
                dispatchUser({ type: 'SetLastNameFilterVisiblity', isVisible: true });
                break;
            case 'IsDisabled':
                dispatchUser({ type: 'SetIsDisabledFilterVisiblity', isVisible: true });
                break;
        }

    }

    const handleMultiSelectFilterApply = (member: keyof (User), values: string[]): void => {
        dispatch(FilterAction.create(member, QueryCondition.Equal, values));
        handleFilterClose();
    };

    const handleFilterClose = () => {
        dispatchUser({ type: 'SetEmailsFilterVisiblity', isVisible: false });
        dispatchUser({ type: 'SetFirstNameFilterVisiblity', isVisible: false });
        dispatchUser({ type: 'SetLastNameFilterVisiblity', isVisible: false });
        dispatchUser({ type: 'SetIsDisabledFilterVisiblity', isVisible: false });
    }

    const handleIsDisabledFilterApply = () => {
        handleMultiSelectFilterApply("IsDisabled", filterValues.IsDisabled);
    }

    const handleFilterClear = (field: keyof User) => {
        handleMultiSelectFilterApply(field, []);
        handleFilterClose();
    }

    const getFilterValues = (member: string, condition: QueryCondition): any =>
        queryTableState.query &&
        queryTableState.query.Filters &&
        queryTableState.query.Filters.find(item => item.Member === member && item.Condition === condition) &&
        queryTableState.query.Filters.find(item => item.Member === member && item.Condition === condition).Values;

    const renderRoles = (row: User) => {
        if (!row.Roles?.length) return '-';
        return row.Roles.map(r => (`(${r.Study ? r.Study.Name : 'All Studies'}) ${r.Name}`)).join(', ');
    }

    const renderStatus = (row: User) => {
        return row.IsDisabled ? <FontAwesomeIcon className="text-danger" icon={faXmark} /> : <FontAwesomeIcon className="text-success" icon={faCheck} />;
    }

    const renderHamburgerMenu = (row: User) => {
        return (
            <HamburgerMenu>
                <GlobalAuthorization permissionId={PermissionEnum.EditUsers} currentPermissions={currentUserPermissions}>
                    <Condition test={() => !row.IsServiceAccount}>
                        <li>
                            <Link to={`/users/edit/${row.Id}`}>Edit</Link>
                        </li>
                    </Condition>
                </GlobalAuthorization>
            </HamburgerMenu>
        );
    }

    return (
        <>
            <QueryTable id="usersTable" query={queryTableState.query} rowUniqueIdentifier={row => row.Id} onFilter={handleFilter} onSort={(m) => handleSort(m)} onPage={(p) => handlePage(p)}>
                <QueryTableColumn member="Email" header="Email" map={row => row.Email} sortable={true} filterable={true} />
                <QueryTableColumn member="FirstName" header="First Name" map={row => row.FirstName} sortable={true} filterable={true} />
                <QueryTableColumn member="LastName" header="Last Name" map={row => row.LastName} sortable={true} filterable={true} />
                <QueryTableColumn member="Roles.Name" header="Roles" map={row => renderRoles(row)} sortable={false} filterable={false} />
                <QueryTableColumn member="IsDisabled" header="Enabled" map={row => renderStatus(row)} sortable={true} filterable={true} />
                <QueryTableColumn member="" header="" map={row => renderHamburgerMenu(row)} sortable={false} filterable={false} />
            </QueryTable>
            <MultiSelectModal
                header="Email Filter"
                show={currentUserTableState.showEmailFilter}
                options={currentUserTableState.emailFilterQuery.Data}
                valueKey={"Email"}
                labelKey={"Email"}
                values={filterValues.Email}
                onChange={values => setFilterValues(prev => ({
                    ...prev,
                    Email: values.map(v => v.Email)
                }))}
                onApply={() => handleMultiSelectFilterApply("Email", filterValues.Email)}
                onClear={() => handleFilterClear("Email")}
                onClose={handleFilterClose}
            />
            <MultiSelectModal
                header="First Name Filter"
                show={currentUserTableState.showFirstNameFilter}
                options={currentUserTableState.emailFilterQuery?.Data ? uniqBy(currentUserTableState.emailFilterQuery?.Data, (a, b) => a.FirstName === b.FirstName) : []}
                valueKey={"FirstName"}
                labelKey={"FirstName"}
                values={filterValues.FirstName}
                onChange={values => setFilterValues(prev => ({
                    ...prev,
                    FirstName: values.map(v => v.FirstName)
                }))}
                onApply={() => handleMultiSelectFilterApply("FirstName", filterValues.FirstName)}
                onClear={() => handleFilterClear("FirstName")}
                onClose={handleFilterClose}
            />
            <MultiSelectModal
                header="Last Name Filter"
                show={currentUserTableState.showLastNameFilter}
                options={currentUserTableState.emailFilterQuery?.Data ? uniqBy(currentUserTableState.emailFilterQuery?.Data, (a, b) => a.LastName === b.LastName) : []}
                valueKey={"LastName"}
                labelKey={"LastName"}
                values={filterValues.LastName}
                onChange={values => setFilterValues(prev => ({
                    ...prev,
                    LastName: values.map(v => v.LastName)
                }))}
                onApply={() => handleMultiSelectFilterApply("LastName", filterValues.LastName)}
                onClear={() => handleFilterClear("LastName")}
                onClose={handleFilterClose}
            />

            <BooleanSelectModal
                header="Enabled Filter"
                options={[
                    { id: true, name: 'No' },
                    { id: false, name: 'Yes' },
                ]}
                show={currentUserTableState.showIsDisabledFilter}
                value={filterValues.IsDisabled}
                onChange={values => setFilterValues(prev => ({
                    ...prev,
                    IsDisabled: values
                }))}
                onApply={() => handleIsDisabledFilterApply()}
                onClear={() => handleFilterClear("IsDisabled")}
                onClose={handleFilterClose}
            />
        </>
    );
}

function mapStateToProps(state: MainState) {
    return {
        currentStudyId: state.security.studyId,
    }
}

export default connect(mapStateToProps)(UserListTable);
